import React, { useEffect, useState, createElement } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { User } from '../../services/authService';
import { Match } from '../../services/matchingService';
import { sendDataByEmail } from '../../services/emailService';
import { UsersIcon, PackageIcon, CalendarIcon, HandshakeIcon, ArrowDownIcon, ArrowUpIcon, SearchIcon, DownloadIcon, MailIcon } from 'lucide-react';
import { Button } from '../../components/Button';
export function AdminDashboard() {
  const [users, setUsers] = useState<User[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [organizers, setOrganizers] = useState<any[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [activeTab, setActiveTab] = useState<'users' | 'brands' | 'organizers' | 'matches'>('users');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isExporting, setIsExporting] = useState(false);
  const [exportFeedback, setExportFeedback] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    visible: boolean;
  }>({
    message: '',
    type: 'info',
    visible: false
  });
  useEffect(() => {
    const loadData = async () => {
      // Load all data from localStorage
      const usersData = JSON.parse(localStorage.getItem('users') || '[]');
      const brandsData = JSON.parse(localStorage.getItem('brands') || '[]');
      const organizersData = JSON.parse(localStorage.getItem('organizers') || '[]');
      const matchesData = JSON.parse(localStorage.getItem('matches') || '[]');
      setUsers(usersData);
      setBrands(brandsData);
      setOrganizers(organizersData);
      setMatches(matchesData);
      setLoading(false);
    };
    loadData();
  }, []);
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const sortData = (data: any[]) => {
    if (!sortField) return data;
    return [...data].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      // Handle nested fields (e.g., "user.name")
      if (sortField.includes('.')) {
        const parts = sortField.split('.');
        aValue = parts.reduce((obj, key) => obj?.[key], a);
        bValue = parts.reduce((obj, key) => obj?.[key], b);
      }
      // Handle dates
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortDirection === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
      }
      // Handle strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      // Handle numbers
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  };
  const filterData = (data: any[]) => {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(item => {
      // Search in all string properties
      return Object.values(item).some(value => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(term);
        }
        return false;
      });
    });
  };
  const exportData = (dataType: 'users' | 'brands' | 'organizers' | 'matches') => {
    let data;
    let filename;
    switch (dataType) {
      case 'users':
        data = users;
        filename = 'users.json';
        break;
      case 'brands':
        data = brands;
        filename = 'brands.json';
        break;
      case 'organizers':
        data = organizers;
        filename = 'organizers.json';
        break;
      case 'matches':
        data = matches;
        filename = 'matches.json';
        break;
    }
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const emailData = async (dataType: 'users' | 'brands' | 'organizers' | 'matches') => {
    setIsExporting(true);
    setExportFeedback({
      message: 'Sending data to your email...',
      type: 'info',
      visible: true
    });
    let data;
    let subject;
    switch (dataType) {
      case 'users':
        data = users;
        subject = 'SponsrAI Users Data Export';
        break;
      case 'brands':
        data = brands;
        subject = 'SponsrAI Brands Data Export';
        break;
      case 'organizers':
        data = organizers;
        subject = 'SponsrAI Organizers Data Export';
        break;
      case 'matches':
        data = matches;
        subject = 'SponsrAI Matches Data Export';
        break;
    }
    try {
      await sendDataByEmail('preslavnikolov@outlook.com', subject, dataType, data);
      setExportFeedback({
        message: `${dataType.charAt(0).toUpperCase() + dataType.slice(1)} data successfully sent to preslavnikolov@outlook.com`,
        type: 'success',
        visible: true
      });
      // Hide feedback after 5 seconds
      setTimeout(() => {
        setExportFeedback(prev => ({
          ...prev,
          visible: false
        }));
      }, 5000);
    } catch (error) {
      setExportFeedback({
        message: `Error sending email: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error',
        visible: true
      });
    } finally {
      setIsExporting(false);
    }
  };
  const renderSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 ml-1" data-id="element-1269" /> : <ArrowDownIcon className="h-4 w-4 ml-1" data-id="element-1270" />;
  };
  if (loading) {
    return <DashboardLayout userType="admin" data-id="element-1271">
        <div className="flex justify-center items-center h-64" data-id="element-1272">
          <div className="text-gray-500" data-id="element-1273">Loading...</div>
        </div>
      </DashboardLayout>;
  }
  return <DashboardLayout userType="admin" data-id="element-1274">
      <div className="mb-6" data-id="element-1275">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1276">Admin Dashboard</h1>
        <p className="text-gray-600" data-id="element-1277">
          Manage users, brands, organizers, and matches on the SponsrAI
          platform.
        </p>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6" data-id="element-1278">
        <div className={`bg-white rounded-lg shadow-sm p-6 cursor-pointer ${activeTab === 'users' ? 'ring-2 ring-indigo-500' : ''}`} onClick={() => setActiveTab('users')} data-id="element-1279">
          <div className="flex items-center" data-id="element-1280">
            <div className="bg-indigo-100 rounded-md p-3" data-id="element-1281">
              <UsersIcon className="h-6 w-6 text-indigo-600" data-id="element-1282" />
            </div>
            <div className="ml-4" data-id="element-1283">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1284">Users</h3>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1285">{users.length}</p>
            </div>
          </div>
        </div>
        <div className={`bg-white rounded-lg shadow-sm p-6 cursor-pointer ${activeTab === 'brands' ? 'ring-2 ring-indigo-500' : ''}`} onClick={() => setActiveTab('brands')} data-id="element-1286">
          <div className="flex items-center" data-id="element-1287">
            <div className="bg-blue-100 rounded-md p-3" data-id="element-1288">
              <PackageIcon className="h-6 w-6 text-blue-600" data-id="element-1289" />
            </div>
            <div className="ml-4" data-id="element-1290">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1291">Brands</h3>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1292">
                {brands.length}
              </p>
            </div>
          </div>
        </div>
        <div className={`bg-white rounded-lg shadow-sm p-6 cursor-pointer ${activeTab === 'organizers' ? 'ring-2 ring-indigo-500' : ''}`} onClick={() => setActiveTab('organizers')} data-id="element-1293">
          <div className="flex items-center" data-id="element-1294">
            <div className="bg-green-100 rounded-md p-3" data-id="element-1295">
              <CalendarIcon className="h-6 w-6 text-green-600" data-id="element-1296" />
            </div>
            <div className="ml-4" data-id="element-1297">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1298">
                Organizers
              </h3>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1299">
                {organizers.length}
              </p>
            </div>
          </div>
        </div>
        <div className={`bg-white rounded-lg shadow-sm p-6 cursor-pointer ${activeTab === 'matches' ? 'ring-2 ring-indigo-500' : ''}`} onClick={() => setActiveTab('matches')} data-id="element-1300">
          <div className="flex items-center" data-id="element-1301">
            <div className="bg-purple-100 rounded-md p-3" data-id="element-1302">
              <HandshakeIcon className="h-6 w-6 text-purple-600" data-id="element-1303" />
            </div>
            <div className="ml-4" data-id="element-1304">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1305">Matches</h3>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1306">
                {matches.length}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Search, Export and Email */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6" data-id="element-1307">
        <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-1308">
          <div className="relative w-full md:w-64 mb-4 md:mb-0" data-id="element-1309">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1310">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1311" />
            </div>
            <input type="text" placeholder="Search..." className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1312" />
          </div>
          <div className="flex space-x-2" data-id="element-1313">
            <Button variant="outline" className="flex items-center" onClick={() => exportData(activeTab)} data-id="element-1314">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-1315" />
              Download {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </Button>
            <Button variant="primary" className="flex items-center" onClick={() => emailData(activeTab)} disabled={isExporting} data-id="element-1316">
              {isExporting ? <span className="inline-block w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1317"></span> : <MailIcon className="h-4 w-4 mr-2" data-id="element-1318" />}
              Email to preslavnikolov@outlook.com
            </Button>
          </div>
        </div>
        {/* Feedback message */}
        {exportFeedback.visible && <div className={`mt-4 p-3 rounded-md ${exportFeedback.type === 'success' ? 'bg-green-100 text-green-800' : exportFeedback.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-1319">
            {exportFeedback.message}
          </div>}
      </div>
      {/* Data Tables */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-1320">
        {activeTab === 'users' && <div className="overflow-x-auto" data-id="element-1321">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1322">
              <thead className="bg-gray-50" data-id="element-1323">
                <tr data-id="element-1324">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('id')} data-id="element-1325">
                    <div className="flex items-center" data-id="element-1326">
                      ID {renderSortIcon('id')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('name')} data-id="element-1327">
                    <div className="flex items-center" data-id="element-1328">
                      Name {renderSortIcon('name')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('email')} data-id="element-1329">
                    <div className="flex items-center" data-id="element-1330">
                      Email {renderSortIcon('email')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('type')} data-id="element-1331">
                    <div className="flex items-center" data-id="element-1332">
                      Type {renderSortIcon('type')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('createdAt')} data-id="element-1333">
                    <div className="flex items-center" data-id="element-1334">
                      Created At {renderSortIcon('createdAt')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1335">
                {filterData(sortData(users)).map(user => <tr key={user.id} data-id="element-1336">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1337">
                      {user.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1338">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1339">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1340">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.type === 'brand' ? 'bg-blue-100 text-blue-800' : user.type === 'organizer' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-1341">
                        {user.type.charAt(0).toUpperCase() + user.type.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1342">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                  </tr>)}
                {filterData(sortData(users)).length === 0 && <tr data-id="element-1343">
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1344">
                      No users found
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}
        {activeTab === 'brands' && <div className="overflow-x-auto" data-id="element-1345">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1346">
              <thead className="bg-gray-50" data-id="element-1347">
                <tr data-id="element-1348">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('companyName')} data-id="element-1349">
                    <div className="flex items-center" data-id="element-1350">
                      Company {renderSortIcon('companyName')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('contactName')} data-id="element-1351">
                    <div className="flex items-center" data-id="element-1352">
                      Contact {renderSortIcon('contactName')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('industry')} data-id="element-1353">
                    <div className="flex items-center" data-id="element-1354">
                      Industry {renderSortIcon('industry')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('productName')} data-id="element-1355">
                    <div className="flex items-center" data-id="element-1356">
                      Product {renderSortIcon('productName')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('budget')} data-id="element-1357">
                    <div className="flex items-center" data-id="element-1358">
                      Budget {renderSortIcon('budget')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('createdAt')} data-id="element-1359">
                    <div className="flex items-center" data-id="element-1360">
                      Created At {renderSortIcon('createdAt')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1361">
                {filterData(sortData(brands)).map(brand => <tr key={brand.id} data-id="element-1362">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1363">
                      {brand.companyName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1364">
                      {brand.contactName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1365">
                      {brand.industry === 'food_beverage' ? 'Food & Beverage' : brand.industry === 'beauty_cosmetics' ? 'Beauty & Cosmetics' : brand.industry === 'health_wellness' ? 'Health & Wellness' : brand.industry === 'tech' ? 'Technology' : brand.industry === 'fashion' ? 'Fashion' : brand.industry === 'home_goods' ? 'Home Goods' : brand.industry === 'sports_fitness' ? 'Sports & Fitness' : brand.industry === 'entertainment' ? 'Entertainment' : 'Other'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1366">
                      {brand.productName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1367">
                      {brand.budget === 'under_10000' ? 'Under 10,000 kr' : brand.budget === '10000_50000' ? '10,000 - 50,000 kr' : brand.budget === '50000_100000' ? '50,000 - 100,000 kr' : brand.budget === '100000_250000' ? '100,000 - 250,000 kr' : brand.budget === '250000_plus' ? '250,000+ kr' : 'Not specified'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1368">
                      {new Date(brand.createdAt).toLocaleDateString()}
                    </td>
                  </tr>)}
                {filterData(sortData(brands)).length === 0 && <tr data-id="element-1369">
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1370">
                      No brands found
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}
        {activeTab === 'organizers' && <div className="overflow-x-auto" data-id="element-1371">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1372">
              <thead className="bg-gray-50" data-id="element-1373">
                <tr data-id="element-1374">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('organizerName')} data-id="element-1375">
                    <div className="flex items-center" data-id="element-1376">
                      Organizer {renderSortIcon('organizerName')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('eventName')} data-id="element-1377">
                    <div className="flex items-center" data-id="element-1378">
                      Event {renderSortIcon('eventName')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('eventType')} data-id="element-1379">
                    <div className="flex items-center" data-id="element-1380">
                      Type {renderSortIcon('eventType')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('eventDate')} data-id="element-1381">
                    <div className="flex items-center" data-id="element-1382">
                      Date {renderSortIcon('eventDate')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('location')} data-id="element-1383">
                    <div className="flex items-center" data-id="element-1384">
                      Location {renderSortIcon('location')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('attendeeCount')} data-id="element-1385">
                    <div className="flex items-center" data-id="element-1386">
                      Attendees {renderSortIcon('attendeeCount')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1387">
                {filterData(sortData(organizers)).map(organizer => <tr key={organizer.id} data-id="element-1388">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1389">
                      {organizer.organizerName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1390">
                      {organizer.eventName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1391">
                      {organizer.eventType.charAt(0).toUpperCase() + organizer.eventType.slice(1)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1392">
                      {new Date(organizer.eventDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1393">
                      {organizer.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1394">
                      {organizer.attendeeCount === 'under_100' ? 'Under 100' : organizer.attendeeCount === '100_500' ? '100 - 500' : organizer.attendeeCount === '500_1000' ? '500 - 1,000' : organizer.attendeeCount === '1000_5000' ? '1,000 - 5,000' : organizer.attendeeCount === '5000_plus' ? '5,000+' : 'Not specified'}
                    </td>
                  </tr>)}
                {filterData(sortData(organizers)).length === 0 && <tr data-id="element-1395">
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1396">
                      No organizers found
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}
        {activeTab === 'matches' && <div className="overflow-x-auto" data-id="element-1397">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1398">
              <thead className="bg-gray-50" data-id="element-1399">
                <tr data-id="element-1400">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('id')} data-id="element-1401">
                    <div className="flex items-center" data-id="element-1402">
                      ID {renderSortIcon('id')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('score')} data-id="element-1403">
                    <div className="flex items-center" data-id="element-1404">
                      Score {renderSortIcon('score')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('brandId')} data-id="element-1405">
                    <div className="flex items-center" data-id="element-1406">
                      Brand {renderSortIcon('brandId')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('organizerId')} data-id="element-1407">
                    <div className="flex items-center" data-id="element-1408">
                      Organizer {renderSortIcon('organizerId')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')} data-id="element-1409">
                    <div className="flex items-center" data-id="element-1410">
                      Status {renderSortIcon('status')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('createdAt')} data-id="element-1411">
                    <div className="flex items-center" data-id="element-1412">
                      Created At {renderSortIcon('createdAt')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1413">
                {filterData(sortData(matches)).map(match => {
              const brand = brands.find(b => b.id === match.brandId);
              const organizer = organizers.find(o => o.id === match.organizerId);
              return <tr key={match.id} data-id="element-1414">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1415">
                        {match.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1416">
                        <div className="flex items-center" data-id="element-1417">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${match.score >= 80 ? 'bg-green-100 text-green-800' : match.score >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1418">
                            {match.score}%
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1419">
                        {brand ? brand.companyName : 'Unknown Brand'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1420">
                        {organizer ? organizer.eventName : 'Unknown Event'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1421">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${match.status === 'accepted' ? 'bg-green-100 text-green-800' : match.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1422">
                          {match.status.charAt(0).toUpperCase() + match.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1423">
                        {new Date(match.createdAt).toLocaleDateString()}
                      </td>
                    </tr>;
            })}
                {filterData(sortData(matches)).length === 0 && <tr data-id="element-1424">
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1425">
                      No matches found
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}
      </div>
    </DashboardLayout>;
}